import type { StockRow } from "../api";
import { AsOfBadge } from "./AsOfBadge";
import { ConvictionChip } from "./ConvictionBadge";

/**
 * Renders the coverage-signal column (data_spec S10.13, ui_spec A2/A6).
 *
 * Deliberately does NOT draw the generic A2 "signal ring" (colour=direction,
 * arc=confidence): coverage.py's cross-segment blend (the G3 layer that
 * produces a STOCK's signal) always reports confidence=None by design --
 * agreement/dispersion belongs to the within-slot layer only (F6.6), never
 * to the cross-segment one. Drawing a confidence arc here would fabricate a
 * number the backend explicitly refuses to produce. A plain direction dot
 * is used instead; see the build report for this as a flagged A2-vs-data
 * mismatch, not an oversight.
 *
 * THE ONE CARRY-OVER DISCIPLINE this component exists to enforce: a
 * volatility (sigma) signal is RANK-SPACE, never a literal percentage. It
 * is never formatted with a trailing "%", and the scale_note is always
 * reachable (native title tooltip -- a single-line caveat string needs no
 * JS popover).
 */
/** DUAL-WEIGHT SIGNAL (2026-09-21): "equal" renders the unchanged
 * equal-weight baseline (row.signal_equal); "skill" renders the shrunk
 * IC-weighted blend (row.signal_skill_weighted) PLUS a delta chip showing
 * how much skill-weighting shifted it vs equal -- near-zero (shrinkage
 * keeps low-model-count horizons close to equal) is exactly as meaningful
 * a reading as a large shift, so the chip is shown whenever a signal
 * exists, not only when the shift crosses some threshold. */
export function SignalCell({
  row,
  outputType,
  dataAsOf,
  variant = "equal",
}: {
  row: StockRow;
  outputType: "return" | "volatility";
  /** The table's own bars/features frontier -- passed through so this cell
   * can show its OWN signal_as_of only when it genuinely differs (a stale
   * signal is exactly the thing worth flagging inline; a fresh one matching
   * the rest of the table would just be noise repeated on every row). */
  dataAsOf?: string | null;
  variant?: "equal" | "skill";
}) {
  const { signal_equal, signal_skill_weighted, skill_delta,
          signal_scale_note: scaleNote, signal_gaps: gaps, signal_as_of: signalAsOf,
          signal_horizon: horizon, conviction } = row;
  const signal = variant === "skill" ? signal_skill_weighted : signal_equal;

  const direction: "up" | "down" | "flat" | "none" =
    signal === null ? "none" : signal > 0 ? "up" : signal < 0 ? "down" : "flat";

  const valueText =
    signal === null
      ? "—" // em dash, honest "no signal"
      : outputType === "volatility"
        ? signal.toFixed(4) // RANK-SPACE -- never a "%"
        : `${signal >= 0 ? "+" : ""}${(signal * 100).toFixed(2)}%`;

  // TESTED-NO-SIGNAL, DISTINCT FROM A GAP (2026-09-25): a segment where a
  // real build attempt confirmed no forecastable signal (e.g. volatility
  // tiers) is a RESOLVED negative, not an omission -- counting it in the
  // "N gap(s)" badge would read as this stock's coverage being incomplete
  // when it is actually fully resolved for that segment. Kept out of the
  // gap count entirely. ALMOST EVERY stock carries one of these (every
  // stock has a volatility-tier segment, and all 4 tiers are tested-no-
  // signal) -- an always-visible badge here was found to clutter every row
  // of the table (2026-09-26 feedback), so it carries no visible chip at
  // all; its reason surfaces only on hover, via the cell's own `title`.
  const realGaps = gaps.filter((g) => g.gap_type !== "tested_no_signal");
  const testedNoSignal = gaps.filter((g) => g.gap_type === "tested_no_signal");
  const gapsTitle = realGaps.length
    ? realGaps.map((g) => `${g.segment_key}: ${g.reason}`).join("\n")
    : undefined;
  const testedTitle = testedNoSignal.length
    ? testedNoSignal.map((g) => `${g.segment_key}: ${g.reason}`).join("\n")
    : undefined;

  // DELTA CHIP (skill variant only): how much skill-weighting shifted this
  // signal vs equal. |delta| < 0.001 (0.1pp for a return, or the
  // equivalent rank-space gap for volatility) reads as "flat" -- shrinkage
  // (lambda=0.3) keeps low-model-count horizons close to equal by design,
  // so a near-zero delta is an expected, honest reading, not noise to hide.
  const deltaDir: "up" | "down" | "flat" | null =
    variant !== "skill" || skill_delta === null ? null :
    Math.abs(skill_delta) < 0.001 ? "flat" : skill_delta > 0 ? "up" : "down";
  const deltaText = skill_delta === null ? null :
    outputType === "volatility"
      ? `${skill_delta >= 0 ? "+" : ""}${skill_delta.toFixed(4)}`
      : `${skill_delta >= 0 ? "+" : ""}${(skill_delta * 100).toFixed(2)}`;

  return (
    <span className="signal-cell" title={testedTitle}>
      <span className={`signal-dot ${direction}`} />
      <span
        className={`signal-value ${direction === "up" ? "pos" : direction === "down" ? "neg" : direction === "flat" ? "mut" : ""}`}
        title={scaleNote ?? undefined}
      >
        {valueText}
      </span>
      {deltaDir !== null && (
        <span
          className={`delta ${deltaDir}`}
          title="How much skill-weighting shifted this vs equal-weight -- near-zero means the contributing models had similar validated skill (equal was already fine)."
        >
          {deltaText}
        </span>
      )}
      {signal !== null && horizon !== null && (
        <span className="mut" style={{ fontSize: "0.7rem" }}>{horizon}d</span>
      )}
      {signal !== null && <ConvictionChip conviction={conviction} />}
      {outputType === "volatility" && signal !== null && (
        <span className="rank-space-badge" title={scaleNote ?? "rank-space, not a literal volatility percentage"}>
          rank
        </span>
      )}
      {realGaps.length > 0 && (
        <span className="gaps-badge has-gaps" title={gapsTitle}>
          {realGaps.length} gap{realGaps.length > 1 ? "s" : ""}
        </span>
      )}
      {signal !== null && dataAsOf != null && signalAsOf !== dataAsOf && (
        <AsOfBadge date={signalAsOf} reference={dataAsOf} label="signal" />
      )}
    </span>
  );
}
